import { useNavigate } from 'react-router-dom';
import { Shield, Users, Activity, RefreshCw, Radio, Database } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminUsers, useSystemHealth, useSubscriptions, useExpiryCache } from '../../api/hooks';
import { adminApi } from '../../api/client';
import { SectionHeader, StatCard, EmptyState, Spinner } from '../../components/ui';
import { fmtDateTime, cn } from '../../lib/utils';

export default function AdminOverviewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: users,  isLoading: uLoading, refetch } = useAdminUsers();
  const { data: health } = useSystemHealth();
  const { data: subs }   = useSubscriptions();
  const { data: ecData, isLoading: ecLoading, isError: ecError } = useExpiryCache();
  const ecRefreshMut = useMutation({
    mutationFn: adminApi.refreshExpiryCache,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['expiryCache'] }),
  });

  const allUsers = users || [];
  const symbols  = subs?.symbols || (Array.isArray(subs) ? subs : []);

  return (
    <div className="max-w-6xl space-y-6">
      <SectionHeader
        title="Admin Overview"
        subtitle="Platform-wide monitoring"
        actions={
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs font-mono text-amber">
              <Shield size={12} /> Admin Mode
            </span>
            <button onClick={refetch} className="btn-ghost flex items-center gap-1.5 text-xs">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Users"     value={allUsers.length} icon={Users} />
        <StatCard label="Subscribed Symbols" value={symbols.length} icon={Radio} accent={symbols.length > 0 ? 'text-bull' : undefined} />
        <StatCard label="WebSocket"       value={health?.websocket ? 'UP' : 'DOWN'} icon={Activity} accent={health?.websocket ? 'text-bull' : 'text-bear'} />
        <StatCard label="Redis"           value={health?.redis ? 'UP' : 'DOWN'} icon={Database} accent={health?.redis ? 'text-bull' : 'text-bear'} />
      </div>

      {/* System health strip */}
      <div className="panel p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {[
              { label: 'WebSocket',   ok: health?.websocket },
              { label: 'Redis',       ok: health?.redis },
              { label: 'Candle Feed', ok: health?.candle_feed ?? health?.websocket },
              { label: 'Strategy API', ok: health !== undefined },
            ].map(({ label, ok }) => (
              <div key={label} className="flex items-center gap-2 text-xs font-mono">
                <span className={`w-2 h-2 rounded-full ${
                  ok ? 'bg-bull animate-pulse' : ok === false ? 'bg-bear animate-pulse' : 'bg-txt-muted'
                }`} />
                <span className={ok ? 'text-txt-secondary' : ok === false ? 'text-bear' : 'text-txt-muted'}>
                  {label}
                </span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/admin/health')} className="btn-ghost text-xs">
            Full Health →
          </button>
        </div>
      </div>

      {/* Symbol subscriptions */}
      {symbols.length > 0 && (
        <div className="panel p-4">
          <SectionHeader title="Live Symbol Feed" subtitle={`${symbols.length} symbols subscribed`} />
          <div className="flex flex-wrap gap-2">
            {symbols.map((s) => (
              <span key={s} className="badge bg-bull/10 border border-bull/30 text-bull font-mono">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Expiry Cache Monitor */}
      <div className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="section-title">Expiry Cache Monitor</p>
          <button
            onClick={() => ecRefreshMut.mutate()}
            disabled={ecRefreshMut.isPending}
            className="btn-ghost text-2xs flex items-center gap-1"
          >
            <RefreshCw size={12} className={ecRefreshMut.isPending ? 'animate-spin' : ''} />
            {ecRefreshMut.isPending ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {ecLoading ? (
          <p className="text-xs font-mono text-txt-muted">Loading expiry cache...</p>
        ) : ecError ? (
          <p className="text-xs font-mono text-bear">Failed to load expiry cache.</p>
        ) : !ecData?.initialized ? (
          <p className="text-xs font-mono text-txt-muted">Expiry cache not initialized.</p>
        ) : (
          Object.entries(ecData.exchanges).map(([exchange, slots]) => (
            <div key={exchange} className="mb-4 last:mb-0">
              <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">{exchange}</p>
              {['CURRENT_WEEK', 'NEXT_WEEK', 'CURRENT_MONTH', 'NEXT_MONTH'].map((slot) => {
                const entry = slots[slot];
                if (!entry) return null;
                return (
                  <div key={slot} className="flex items-center gap-4 py-1 border-b border-bg-border/20 last:border-0">
                    <span className="data-cell w-32">{slot}</span>
                    <span className="mono-value text-xs">{entry.date ?? '—'}</span>
                    <span className="data-cell">{entry.timestamp ?? '—'}</span>
                    <span className="data-cell text-txt-muted">{entry.source ?? '—'}</span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Users table */}
      <div className="panel p-4">
        <SectionHeader
          title="All Users"
          subtitle={`${allUsers.length} registered`}
          actions={
            <button onClick={() => navigate('/admin/users')} className="btn-ghost text-xs">
              Manage →
            </button>
          }
        />
        {uLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : !allUsers.length ? (
          <EmptyState title="No users found" description="Create the first user via the Users page" />
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                {['ID', 'Username', 'Full Name', 'Email', 'Created'].map((h) => (
                  <th key={h} className="text-left py-2 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allUsers.map((u) => (
                <tr key={u.id} className="border-b border-bg-border/30 hover:bg-bg-hover">
                  <td className="py-2.5 px-3 font-mono text-txt-muted">{u.id}</td>
                  <td className="py-2.5 px-3 font-semibold text-txt-primary">{u.username}</td>
                  <td className="py-2.5 px-3 text-txt-secondary">{u.full_name || '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-txt-secondary">{u.email || '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-txt-muted">{fmtDateTime(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
